import React, { useMemo, useRef, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { Check, ChevronDown, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useClickOutside } from "../../../../shared/hooks"

const MultiSelectChips = ({
  options = [],
  value = [],
  onChange,
  placeholder = "",
  emptyText = "—",
  disabled = false,
  maxVisible = 8, // сколько чипов показывать в свёрнутом виде
}) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const rootRef = useRef(null)
  const inputRef = useRef(null)

  useClickOutside(rootRef, () => {
    setOpen(false)
    setQuery("")
  })

  const selectedIds = useMemo(() => new Set(value.map(Number)), [value])
  const selected = useMemo(
    () => options.filter((o) => selectedIds.has(Number(o.id))),
    [options, selectedIds]
  )
  const hiddenCount = Math.max(0, selected.length - maxVisible)
  const visible = expanded || hiddenCount === 0 ? selected : selected.slice(0, maxVisible)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options
  }, [options, query])

  const toggle = (id) => {
    const n = Number(id)
    onChange(selectedIds.has(n) ? value.filter((v) => Number(v) !== n) : [...value, n])
  }

  const remove = (e, id) => {
    e.stopPropagation()
    onChange(value.filter((v) => Number(v) !== Number(id)))
  }

  const handleKeyDown = (e) => {
    if (e.key === "Backspace" && !query && value.length) onChange(value.slice(0, -1))
    if (e.key === "Escape") setOpen(false)
  }

  return (
    <div ref={rootRef} className="relative">
      <div
        onClick={() => {
          if (disabled) return
          setOpen(true)
          inputRef.current?.focus()
        }}
        className={`flex items-start gap-2 min-h-[108px] px-3 py-2.5 rounded-input border bg-surface-main cursor-text transition-colors ${
          open ? "border-brand-main ring-2 ring-brand-main/20" : "border-app-content-faint/30"
        } ${disabled ? "opacity-disabled cursor-not-allowed" : ""}`}
      >
        <div
          className={`flex flex-wrap items-center gap-2 flex-1 min-w-0 ${
            expanded ? "max-h-[280px] overflow-y-auto pr-1" : ""
          }`}
        >
          <AnimatePresence initial={false}>
            {visible.map((o) => (
              <motion.span
                key={o.id}
                layout
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.85 }}
                transition={{ duration: 0.18 }}
                className="inline-flex items-center gap-1.5 max-w-full px-3 py-1.5 rounded-button bg-brand-main/10 text-brand-main text-sm"
              >
                <span className="truncate">{o.name}</span>
                <button
                  type="button"
                  onClick={(e) => remove(e, o.id)}
                  className="shrink-0 hover:opacity-70"
                  aria-label="remove"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </motion.span>
            ))}
          </AnimatePresence>

          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setExpanded((v) => !v)
              }}
              className="inline-flex items-center px-3 py-1.5 rounded-button border border-brand-main/30 text-brand-main text-sm hover:bg-brand-main/5 transition-colors"
            >
              {expanded
                ? t("admin.collapse", { defaultValue: "Свернуть" })
                : t("admin.show_more_count", { count: hiddenCount, defaultValue: "Ещё {{count}}" })}
            </button>
          )}

          <input
            ref={inputRef}
            type="text"
            value={query}
            disabled={disabled}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setQuery(e.target.value)
              setOpen(true)
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 min-w-[120px] bg-transparent outline-none text-app-content placeholder-app-content-faint py-1.5"
          />
        </div>

        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          className="shrink-0 self-center text-app-base-strong"
        >
          <ChevronDown className="w-4 h-4" />
        </motion.span>
      </div>

      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-surface-main rounded-input border border-app-content-faint/20 shadow-md py-1"
          >
            {filtered.length === 0 && (
              <li className="px-4 py-3 text-sm text-text-muted">{emptyText}</li>
            )}
            {filtered.map((o) => {
              const isSelected = selectedIds.has(Number(o.id))
              return (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() => toggle(o.id)}
                    className={`w-full flex items-center justify-between gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-bg-secondary ${
                      isSelected ? "text-brand-main font-medium" : "text-app-content"
                    }`}
                  >
                    <span className="truncate">{o.name}</span>
                    {isSelected && <Check className="w-4 h-4 shrink-0" />}
                  </button>
                </li>
              )
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  )
}

export default MultiSelectChips
